import { money } from "@yn/money";
import type { CurrencyCode, ExchangeRateMap } from "@yn/money/types";

/**
 * Money exchange updater utilities
 *
 * 用途：
 * - 将后端返回的汇率写入全局 money 对象
 * - 支持频繁调用刷新，供全站统一换汇使用
 *
 * 说明：
 * - currency：当前金额所属币种（源币种）
 * - targetCurrency：需要换算到的目标币种
 *
 * 使用示例：
 * ```ts
 * import { money } from "@yn/money";
 * import { refreshMoneyExchangeRates } from "@/utils/money-exchange";
 * import { useExchangeRateStore } from "@/store/exchange-rate";
 *
 * // 1) 主动更新（全局生效）
 * money.updateExchangeRates?.({
 *   base: "USD",
 *   rates: { USD: 1, EUR: 0.92, CNY: 7.2 },
 * });
 *
 * // 2) 通过后端刷新（建议定时/页面进入时调用）
 * await refreshMoneyExchangeRates( async () => {
 *   const [ isOk, res ] = await apiGetExchangeRate();
 *   if ( !isOk ) return null;
 *
 *   // TODO: 按后端实际字段转换为 ExchangeRateMap
 *   return {
 *     base: "USD",
 *     rates: { USD: 1, EUR: 0.92 },
 *   };
 * } );
 *
 * // 3) 组件内订阅（汇率更新自动触发重渲染）
 * const lastUpdated = useExchangeRateStore( ( s ) => s.lastUpdated );
 * ```
 */

type ExchangeRateFetcher = () => Promise<ExchangeRateMap | null>;
type ExchangePair = { currency: CurrencyCode; targetCurrency: CurrencyCode };

const exchangeRateFetchers: Record<string, ExchangeRateFetcher> = {};

function makeExchangePairKey( pair: ExchangePair ) {
    return `${pair.currency}->${pair.targetCurrency}`;
}

function getExchangeService() {
    return ( money as any ).exchange as { rateMap?: ExchangeRateMap } | undefined;
}

/**
 * 直接写入全局汇率表（合并 rates）
 * @param rateMap ExchangeRateMap { base, rates }
 */
export function updateMoneyExchangeRates( rateMap: ExchangeRateMap ) {
    if ( !rateMap?.base || !rateMap?.rates ) return;
    const exchange = getExchangeService();
    if ( !exchange?.rateMap ) return;
    exchange.rateMap.base = rateMap.base;
    exchange.rateMap.rates = { ...exchange.rateMap.rates, ...rateMap.rates };
}

/**
 * 通过异步 fetcher 拉取并更新汇率
 * @param fetcher 返回 ExchangeRateMap 的函数（失败返回 null）
 */
export async function refreshMoneyExchangeRates( fetcher: ExchangeRateFetcher ) {
    if ( !fetcher ) return null;
    const rateMap = await fetcher();
    if ( rateMap ) {
        updateMoneyExchangeRates( rateMap );
    }
    return rateMap ?? null;
}

/**
 * 注册某个币种对的汇率请求器
 * @param pair { currency, targetCurrency }
 * @param fetcher 返回 ExchangeRateMap 的函数
 */
export function registerExchangeRateFetcher( pair: ExchangePair, fetcher: ExchangeRateFetcher ) {
    if ( !pair?.currency || !pair?.targetCurrency || !fetcher ) return;
    exchangeRateFetchers[makeExchangePairKey( pair )] = fetcher;
}

/**
 * 按币种对自动刷新汇率
 */
export async function refreshMoneyExchangeRatesFor( pair: ExchangePair ) {
    if ( !pair?.currency || !pair?.targetCurrency ) return null;
    const key = makeExchangePairKey( pair );
    const fetcher = exchangeRateFetchers[key];
    if ( !fetcher ) return null;
    return refreshMoneyExchangeRates( fetcher );
}

/**
 * 默认模版：从后端获取 ExchangeRateMap
 *
 * 期望后端返回：
 * {
 *   base: "USD",
 *   rates: { USD: 1, EUR: 0.92 }
 * }
 */
/**
 * 将更新方法挂到全局 money 对象上（幂等）
 * - money.updateExchangeRates(rateMap)
 * - money.refreshExchangeRates(fetcher)
 */
export function attachMoneyExchangeUpdater() {
    const moneyWithUpdater = money as typeof money & {
        updateExchangeRates?: ( rateMap: ExchangeRateMap ) => void;
        refreshExchangeRates?: ( fetcher: ExchangeRateFetcher ) => Promise<ExchangeRateMap | null>;
        refreshExchangeRatesFor?: ( pair: ExchangePair ) => Promise<ExchangeRateMap | null>;
        registerExchangeRateFetcher?: ( pair: ExchangePair, fetcher: ExchangeRateFetcher ) => void;
    };

    if ( !moneyWithUpdater.updateExchangeRates ) {
        moneyWithUpdater.updateExchangeRates = updateMoneyExchangeRates;
    }
    if ( !moneyWithUpdater.refreshExchangeRates ) {
        moneyWithUpdater.refreshExchangeRates = refreshMoneyExchangeRates;
    }
    if ( !moneyWithUpdater.refreshExchangeRatesFor ) {
        moneyWithUpdater.refreshExchangeRatesFor = refreshMoneyExchangeRatesFor;
    }
    if ( !moneyWithUpdater.registerExchangeRateFetcher ) {
        moneyWithUpdater.registerExchangeRateFetcher = registerExchangeRateFetcher;
    }
}

// 自动挂载，确保任意引入 utils 时可直接使用 money.updateExchangeRates
attachMoneyExchangeUpdater();

// async function fetchExchangeRateTemplate() {
//     const [ isOk, res ] = await apiGetExchangeRate();
//     if ( !isOk ) return null;
//     const data = ( res as any )?.data as ExchangeRateMap | undefined;
//     if ( !data?.base || !data?.rates ) return null;
//     return data;
// }

// 默认模版：source -> USD
// registerExchangeRateFetcher(
//     { currency: "EUR", targetCurrency: "USD" },
//     fetchExchangeRateTemplate,
// );
